// Construção pura: dados (team + members) → {nodes, edges} com posições determinísticas.
// Sem dagre/elk: layout LR-n8n em 3 colunas (supervisor · subagentes · tools),
// com reserva de bloco por subárvore (evita colisão entre famílias de tools).

import type { TeamSummary, AgentSummary, ToolSummary } from '../../types/agno-api';

export type Kind = 'team' | 'agent' | 'tool';

export interface FlowNodeModel {
  id: string; // team.id | agent.id | `${agentId}::${tool.name}`
  kind: Kind;
  payload: TeamSummary | AgentSummary | ToolSummary;
  // Para tool-node: o agente dono (clique no tool abre o painel do agente).
  parentAgent?: AgentSummary;
  parentId?: string; // edge target←source: agent→team.id; tool→agentId
  colorIndex?: number; // 1..5 (chart-N) — agentes e suas tools herdam
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface FlowEdgeModel {
  id: string;
  sourceId: string;
  targetId: string;
  colorIndex?: number;
  kind: 'team-agent' | 'agent-tool';
}

export interface FlowGraph {
  nodes: FlowNodeModel[];
  edges: FlowEdgeModel[];
}

// Constantes de layout.
// AGENT_W/AGENT_H e TEAM_W/TEAM_H foram alargados/altos o bastante para caber
// nome (1 linha) + role/descrição (até 2 linhas com line-clamp-2) sem transbordar.
// COL_X[1]/COL_X[2] acompanham a largura maior dos cards.
export const COL_X = [0, 400, 780] as const;
export const AGENT_W = 280;
export const AGENT_H = 116;
export const GAP_V = 28;
export const TOOL_W = 200;
export const TOOL_H = 36;
export const TOOL_GAP = 10;
export const TEAM_W = 280;
export const TEAM_H = 116;

// Ordem de ciclagem das cores chart-N: cores saturadas primeiro, chart-3 (azul
// escuro, baixo contraste no dark) por último — só "sobra" para o 5º+ agente.
const COLOR_CYCLE = [1, 2, 5, 4, 3] as const;

function colorFor(i: number): number {
  return COLOR_CYCLE[i % COLOR_CYCLE.length];
}

// O AgentOS devolve `tools` ora como array, ora como objeto `{ tools: [...] }`
// (o caso real do Salvador). Normaliza pra array em qualquer formato — sem isso,
// `.forEach`/`.map`/`.length` quebram e o grafo nem é construído (canvas vazio).
export function normalizeTools(t: unknown): ToolSummary[] {
  if (Array.isArray(t)) return t as ToolSummary[];
  if (t && typeof t === 'object' && Array.isArray((t as { tools?: unknown }).tools)) {
    return (t as { tools: ToolSummary[] }).tools;
  }
  return [];
}

function toolsOf(agent: AgentSummary): ToolSummary[] {
  return normalizeTools(agent.tools);
}

export function buildGraph(
  team: TeamSummary,
  members: AgentSummary[],
  collapsed: boolean,
): FlowGraph {
  const nodes: FlowNodeModel[] = [];
  const edges: FlowEdgeModel[] = [];

  // Altura do bloco de cada agente: colapsado = AGENT_H; expandido reserva as tools.
  const blockH = (agent: AgentSummary): number => {
    if (collapsed) return AGENT_H;
    const n = toolsOf(agent).length;
    const toolsH = n > 0 ? n * (TOOL_H + TOOL_GAP) - TOOL_GAP : 0;
    return Math.max(AGENT_H, toolsH);
  };

  // Empilha blocos sequencialmente (reserva por subárvore).
  let cursorY = 0;
  const agentTops: number[] = [];
  members.forEach((agent) => {
    agentTops.push(cursorY);
    cursorY += blockH(agent) + GAP_V;
  });
  const totalH = cursorY > 0 ? cursorY - GAP_V : AGENT_H;

  // Supervisor: centralizado verticalmente na coluna 0.
  nodes.push({
    id: team.id,
    kind: 'team',
    payload: team,
    x: COL_X[0],
    y: (totalH - TEAM_H) / 2,
    w: TEAM_W,
    h: TEAM_H,
  });

  members.forEach((agent, i) => {
    const colorIndex = colorFor(i);
    const top = agentTops[i];
    // Agente fica no topo do seu bloco (col 1).
    nodes.push({
      id: agent.id,
      kind: 'agent',
      payload: agent,
      parentId: team.id,
      colorIndex,
      x: COL_X[1],
      y: top,
      w: AGENT_W,
      h: AGENT_H,
    });
    edges.push({
      id: `${team.id}->${agent.id}`,
      sourceId: team.id,
      targetId: agent.id,
      colorIndex,
      kind: 'team-agent',
    });

    if (!collapsed) {
      toolsOf(agent).forEach((tool, j) => {
        const toolId = `${agent.id}::${tool.name}`;
        nodes.push({
          id: toolId,
          kind: 'tool',
          payload: tool,
          parentAgent: agent,
          parentId: agent.id,
          colorIndex,
          x: COL_X[2],
          y: top + j * (TOOL_H + TOOL_GAP),
          w: TOOL_W,
          h: TOOL_H,
        });
        edges.push({
          id: `${agent.id}->${toolId}`,
          sourceId: agent.id,
          targetId: toolId,
          colorIndex,
          kind: 'agent-tool',
        });
      });
    }
  });

  return { nodes, edges };
}

// Bounding box de todos os nós (em coords de mundo), usando posições efetivas.
export function bboxOf(
  nodes: FlowNodeModel[],
  posOf: (n: FlowNodeModel) => { x: number; y: number },
): { x: number; y: number; w: number; h: number } {
  if (nodes.length === 0) return { x: 0, y: 0, w: 1, h: 1 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const n of nodes) {
    const p = posOf(n);
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x + n.w);
    maxY = Math.max(maxY, p.y + n.h);
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

// Calcula zoom+pan que enquadra todos os nós no viewport (com padding).
export function computeFit(
  nodes: FlowNodeModel[],
  posOf: (n: FlowNodeModel) => { x: number; y: number },
  viewportW: number,
  viewportH: number,
  clamp: (z: number) => number,
  padding = 48,
): { zoom: number; pan: { x: number; y: number } } {
  const bb = bboxOf(nodes, posOf);
  const availW = Math.max(1, viewportW - padding * 2);
  const availH = Math.max(1, viewportH - padding * 2);
  const z = clamp(Math.min(availW / bb.w, availH / bb.h));
  // Centraliza o bbox no viewport.
  const panX = (viewportW - bb.w * z) / 2 - bb.x * z;
  const panY = (viewportH - bb.h * z) / 2 - bb.y * z;
  return { zoom: z, pan: { x: panX, y: panY } };
}
